/**
 * Simple icon registry using SVG strings
 * Much cleaner approach with string replacement for colors
 */

// Registry storage - just SVG strings
const iconRegistry = new Map<string, string>();

/**
 * Register an icon SVG string in the registry
 */
export const registerIcon = (name: string, svgString: string): void => {
  // Simple color replacement: any hardcoded color → currentColor
  const processedSVG = svgString
    .replace(/#[0-9A-Fa-f]{6}/g, "currentColor") // hex colors
    .replace(/#[0-9A-Fa-f]{3}/g, "currentColor") // short hex
    .replace(/rgb\([^)]+\)/g, "currentColor"); // rgb colors

  iconRegistry.set(name, processedSVG);
};

/**
 * Get SVG string from registry
 */
export const getIconSVG = (name: string): string | undefined => {
  return iconRegistry.get(name);
};

/**
 * Get all registered icon names
 */
export const getIconNames = (): readonly string[] => {
  return Array.from(iconRegistry.keys());
};

/**
 * Auto-register all icons from the icons directory
 * Dynamically discovers all SVG files instead of manual list
 */
export const initializeIconRegistry = async (): Promise<void> => {
  // All your available icons - exported from Figma with clean naming
  const allIconNames = [
    "alignment_bottom",
    "alignment_center",
    "alignment_left",
    "alignment_middles",
    "alignment_right",
    "alignment_top",
    "ballpoint_medium",
    "ballpoint_thick",
    "ballpoint_thin",
    "breakpoints_desktop",
    "breakpoints_desktop_large",
    "breakpoints_mobile",
    "breakpoints_tablet",
    "distribute_hor",
    "distribute_tidy_hor",
    "distribute_tidy_vert",
    "distribute_vert",
    "erasers_clean",
    "erasers_eraser",
    "erasers_new_canvas",
    "helpers_dots",
    "helpers_guides",
    "helpers_lines",
    "helpers_rulers",
    "helpers_snap",
    "helpers_overlay",
    "interactions_annotate",
    "interactions_audio",
    "interactions_mic",
    "interactions_mute",
    "llm_llm",
    "markers_medium",
    "markers_thin",
    "modes_cms",
    "modes_ds",
    "modes_edit",
    "modes_sketch",
    "modifiers_alt",
    "modifiers_redo",
    "modifiers_shift",
    "modifiers_undo",
    "page_all",
    "page_home",
    "page_slug",
    "project_web",
    "project_dashboard",
    "project_app",
    "prefs_project",
    "prefs_settings",
    "preview_play",
    "preview_stop",
    "preview_refresh",
    "project_web",
    "project_dashboard",
    "project_app",
    "themes_light",
    "themes_dark",
    "tools_component",
    "tools_inspect",
    "tools_marquee",
    "tools_lasso",
    "tools_layout_columns",
    "tools_layout_frame",
    "tools_layout_grid",
    "tools_layout_rows",
    "tools_media",
    "tools_shape_ovel",
    "tools_shape_polygon",
    "tools_shape_rectangle",
    "tools_shape_star",
    "tools_text",
    "zoom_1_1",
    "zoom_all",
    "zoom_element",
    "zoom_height",
    "zoom_width",
    "unna_logo",
  ];

  console.log(`🎨 Loading ${allIconNames.length} icons...`);
  let loadedCount = 0;

  for (const iconName of allIconNames) {
    try {
      const iconUrl = new URL(`./icons/${iconName}.svg`, import.meta.url).href;
      const response = await fetch(iconUrl);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const svgString = await response.text();
      registerIcon(iconName, svgString);
      loadedCount++;
      console.log(`✅ ${iconName}`);
    } catch (error) {
      console.warn(`❌ Failed to load ${iconName}:`, error);
    }
  }

  console.log(
    `📦 Successfully loaded ${loadedCount}/${allIconNames.length} icons`
  );
};
